// @ts-nocheck
import React, { useContext, useEffect, useRef, useState } from 'react';
import FixedButton from '@core-ui/fixed-button';
import { DrawerSlidePosition } from '@core-ui/motion';
import { useLocalize } from '@core-ui/localization-provider';
// import { useSitewide } from '@mfe/sitewide';
import { Drawer } from '@core-ui/drawer';
import { BreakpointContext, XLARGE } from '@core-ui/breakpoint-provider';
import { useAppState } from '@core-ui/app-state-provider';
import { css, getFontWeight, Theme } from '@core-ui/react-stitch';
import {
  isDashboard,
  isDrawer,
  isTop,
  Position,
} from '../../collaborators/positions';
import scrollOnClose from '../../collaborators/scroll-on-close';
import FilterIcon from './components/FilterIcon';
import CloseButton from './styled/CloseButton';
import Dashboard from './styled/Dashboard';
import DashboardItem from './styled/DashboardItem';
import DashboardTitle from './styled/DashboardTitle';
import DrawerContent from './styled/DrawerContent';
import Divider from './styled/Divider';
import {
  DASHBOARD_FILTER,
  MOBILE_FACETS_WRAPPER_DONE_TEXT,
  FILTERS_ARIA_LABEL,
  CLOSE_ARIA_LABEL,
} from './translations';

interface MobileProps {
  children: ComponentProps[];
  newDashboard: boolean;
  isBRRedesignEnabled: boolean;
  isBRRedesignOpacityEnabled: boolean;
  isModelToggleEnabled: boolean;
  hasMarketingDataEBB?: boolean;
  appliedFacetsCount: number;
}

interface ComponentProps extends Node {
  props: {
    htmlFor?: string;
    title?: string;
    position: Position;
  };
}

// eslint-disable-next-line complexity
const Mobile = (props: MobileProps) => {
  const {
    children,
    appliedFacetsCount,
    newDashboard = false,
    isBRRedesignEnabled = false,
    isBRRedesignOpacityEnabled = false,
    isModelToggleEnabled = false,
    hasMarketingDataEBB = false,
  } = props;

  const { brandName } = useAppState();
  const isBR = brandName === 'br' || brandName === 'brfs';

  // const { totalStickyElementHeight } = useSitewide();
  const { greaterOrEqualTo } = useContext(BreakpointContext);
  // If rendering BR redesign, this Mobile component is rendered for desktop
  const isDesktop = greaterOrEqualTo(XLARGE);

  const mobileWrapper = () => {
    return hasMarketingDataEBB && isBRRedesignEnabled
      ? { marginTop: '-45px', zIndex: '100' }
      : {};
  };

  const mobileStyles = () =>
    css({
      width: '100%',
      padding: '0 1rem',
      ...(isBRRedesignEnabled
        ? {
            position: 'sticky',
            // top: totalStickyElementHeight,
            top: isDesktop ? 118 : 79,
            zIndex: '100',
            padding: '0',
          }
        : {}),
    });

  const triggerButtonCSS = (theme: Theme) =>
    css({
      background: 'none',
      border: 'none',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      outline: 'none',
      color: theme.color.b1,
      textTransform: 'uppercase',
      paddingLeft: '0',
      margin: '0.25rem 0',
      '& svg': {
        marginRight: '0.2rem',
      },
      ...(newDashboard
        ? {}
        : {
            ...theme.brandFont,
            fontSize: '0.9rem',
            fontWeight: isBR ? getFontWeight('light').fontWeight : 'bold',
            color: theme.color.b1,
            background: 'none',
            border: 'none',
            margin: '0',
            textTransform: 'uppercase',
            padding: 'unset',
          }),
      ...(isBRRedesignEnabled
        ? {
            '&:hover': {
              backgroundColor: theme.color.b2,
            },
            backgroundColor: `${theme.color.b2}${
              isBRRedesignOpacityEnabled ? '9E' : '00'
            }`,
            color: theme.color.inverse.b1,
            textTransform: 'capitalize',
            fontWeight: getFontWeight('book').fontWeight,
          }
        : {}),
    });

  const drawerBottomRef = useRef<HTMLDivElement>(null);

  const [shouldScroll, setShouldScroll] = useState(false);
  const [drawerBottomOffsetHeight, setDrawerBottomOffsetHeight] = useState(0);
  const [open, setOpen] = useState(false);

  const openDrawer = (): void => {
    setOpen(true);
    setShouldScroll(!isBRRedesignEnabled);

    setTimeout(() => {
      setDrawerBottomOffsetHeight(drawerBottomRef?.current?.offsetHeight ?? 0);
    }, 1000);
  };

  const closeDrawer = (): void => {
    setOpen(false);
    setShouldScroll(!isBRRedesignEnabled);
  };

  const { localize } = useLocalize();
  const components = React.Children.map(children, (child) => child) ?? [];
  const topComponents = components.filter(isTop);
  const dashboardComponents = components.filter(isDashboard);
  const drawerComponents = components.filter(isDrawer);
  const showFacetCountLabel =
    Number.isInteger(appliedFacetsCount) && appliedFacetsCount >= 1
      ? `(${appliedFacetsCount})`
      : '';
  const filterText = localize(DASHBOARD_FILTER);
  const triggerButtonPrefix = newDashboard ? (
    <FilterIcon />
  ) : (
    filterText?.toLowerCase()
  );
  const triggerButtonText = (
    <>
      {triggerButtonPrefix} {showFacetCountLabel}
    </>
  );

  const drawerPosition = isBRRedesignEnabled
    ? DrawerSlidePosition.left
    : DrawerSlidePosition.right;

  useEffect(() => {
    if (shouldScroll) {
      scrollOnClose(setShouldScroll);
    }
  }, [open, shouldScroll]);

  return (
    <div css={mobileStyles} style={mobileWrapper()}>
      {topComponents}
      <Dashboard
        isBrRedesignEnabled={isBRRedesignEnabled}
        newDashboard={newDashboard}
      >
        <DashboardItem
          isBrRedesignEnabled={isBRRedesignEnabled}
          isModelToggleEnabled={isModelToggleEnabled}
          newDashboard={newDashboard}
        >
          <div className="filter-container">
            {newDashboard && (
              <DashboardTitle htmlFor="filterButton" withChevron={false}>
                {filterText}
              </DashboardTitle>
            )}
            <button
              className="filter_button"
              css={triggerButtonCSS}
              id="filterButton"
              onClick={openDrawer}
              type="button"
            >
              {triggerButtonText}
            </button>
            <Drawer
              aria-label={localize(FILTERS_ARIA_LABEL)}
              container=".cat-drawer"
              dialogCSS={(theme) => ({
                width: '80%',
                maxWidth: '20rem',
                ...(isBRRedesignEnabled && {
                  backgroundColor: theme.color.inverse.b1,
                  maxWidth: '20.3129rem',
                }),
              })}
              id="facets"
              isOpen={open}
              onClose={closeDrawer}
              position={drawerPosition}
            >
              <DrawerContent>
                <CloseButton
                  ariaLabel={localize(CLOSE_ARIA_LABEL)}
                  buttonSide={isBRRedesignEnabled ? 'right' : 'left'}
                  onClick={closeDrawer}
                />
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: `calc(100% - ${drawerBottomOffsetHeight}px)`,
                  }}
                >
                  {drawerComponents}
                </div>
                <div
                  ref={drawerBottomRef}
                  css={(theme) => ({
                    padding: '1rem',
                    borderTop: `1px solid ${theme.color.gray20}`,
                  })}
                >
                  <FixedButton
                    css={(theme) => ({
                      ...(isBRRedesignEnabled && {
                        backgroundColor: theme.color.b2,
                        color: theme.color.inverse.b1,
                      }),
                    })}
                    kind="primary"
                    onClick={closeDrawer}
                  >
                    {localize(MOBILE_FACETS_WRAPPER_DONE_TEXT)}
                  </FixedButton>
                </div>
              </DrawerContent>
            </Drawer>
          </div>
        </DashboardItem>
        {dashboardComponents.map((component, key) => (
          <React.Fragment key={key}>
            {newDashboard && isModelToggleEnabled ? <Divider /> : null}
            <DashboardItem
              isModelToggleEnabled={isModelToggleEnabled}
              newDashboard={newDashboard}
            >
              {newDashboard && (
                <DashboardTitle
                  htmlFor={component.props?.children?.props?.htmlFor}
                >
                  {component.props.title}
                </DashboardTitle>
              )}
              {component}
            </DashboardItem>
          </React.Fragment>
        ))}
      </Dashboard>
    </div>
  );
};

export default Mobile;
